import { create } from 'zustand';
import type {
  AttentionEntry,
  ConditionType,
  HpTier,
  Open5eMonster,
} from '@machina/core';
import { getHpTier, getModifier } from '@machina/core';
import { affinityMultiplier, affinityVerdict, parseAffinityList } from '@machina/core';
import type { DamageAffinity } from '@machina/core';
import type { Token } from '@machina/core';
import { useTokenStore } from './tokenStore';

import { useCombatStore } from './combatStore';
import { useInitiativeStore } from './initiativeStore';
import { trpc } from '../lib/trpc';
import { supabase } from '../lib/supabase';
import { createSubscription } from '../lib/realtimeHelpers';
import { resolveMobImage, generateMissingTokens } from '../lib/assetPaths';
import { useUndoStore } from './undoStore';
import { persistWithRetry } from '../lib/persistence';
import { isCurrentUserGM } from '../hooks/useIsGM';
import { parseOpen5eAction } from '../combat/encounter-srd';

// =============================================================================
// TYPES
// =============================================================================

/**
 * A single attack/action a mob can take
 */
export interface MobAction {
  name: string;
  attackBonus: number;
  damageDice: string; // "2d6+3"
  damageType: string; // "slashing", "fire", etc.
  range: 'melee' | 'ranged';
  description?: string;
}

/**
 * Condition instance with optional duration/source
 */
export interface MobCondition {
  type: ConditionType;
  sourceId?: string; // Who applied it
  duration?: number; // Rounds remaining, if any
}

/**
 * Mob IQ tiers - affects decision quality
 * Lower IQ = more mistakes, suboptimal choices
 */
export type MobIQTier = 'mindless' | 'beast' | 'dim' | 'average' | 'cunning' | 'genius';

/**
 * Get IQ tier from intelligence score
 */
export function getMobIQTier(intelligence: number): MobIQTier {
  if (intelligence <= 2) return 'mindless';  // Oozes, zombies
  if (intelligence <= 5) return 'beast';      // Animals, basic undead
  if (intelligence <= 8) return 'dim';        // Goblins, ogres
  if (intelligence <= 12) return 'average';   // Most humanoids
  if (intelligence <= 16) return 'cunning';   // Smart enemies
  return 'genius';                             // Dragons, liches
}

/**
 * Mistake chance by IQ tier (0-1)
 * Higher = more likely to make suboptimal move
 */
export const IQ_MISTAKE_CHANCE: Record<MobIQTier, number> = {
  mindless: 0.7,  // 70% chance of random/suboptimal move
  beast: 0.5,     // 50% - instinct-driven
  dim: 0.3,       // 30% - occasionally dumb
  average: 0.1,   // 10% - rarely makes mistakes
  cunning: 0.02,  // 2% - almost never
  genius: 0,      // Perfect play
};

/**
 * IQ penalties from conditions
 * Applied as step-downs in IQ tier
 */
export const CONDITION_IQ_PENALTY: Partial<Record<ConditionType, number>> = {
  poisoned: 1,      // -1 tier (average -> dim)
  frightened: 1,    // -1 tier (panic clouds judgment)
  charmed: 2,       // -2 tiers (following someone else's will)
  stunned: 99,      // Effectively mindless
  incapacitated: 99,
  paralyzed: 99,
  unconscious: 99,
};

/**
 * Calculate effective IQ tier accounting for conditions
 */
export function getEffectiveIQ(baseIntelligence: number, conditions: MobCondition[]): MobIQTier {
  const tiers: MobIQTier[] = ['mindless', 'beast', 'dim', 'average', 'cunning', 'genius'];
  const baseTier = getMobIQTier(baseIntelligence);
  let tierIndex = tiers.indexOf(baseTier);

  // Apply condition penalties
  for (const condition of conditions) {
    const penalty = CONDITION_IQ_PENALTY[condition.type] || 0;
    tierIndex -= penalty;
  }

  // Clamp to valid range
  tierIndex = Math.max(0, Math.min(tiers.length - 1, tierIndex));
  return tiers[tierIndex];
}

/**
 * Get current mistake chance for a mob (accounts for conditions)
 */
export function getMobMistakeChance(mob: Mob): number {
  const effectiveIQ = getEffectiveIQ(mob.intelligence, mob.conditions);
  return IQ_MISTAKE_CHANCE[effectiveIQ];
}

/**
 * Tactical preferences for mob AI
 */
export interface MobTactics {
  // Mob IQ - affects decision quality
  iq: MobIQTier;
  mistakeChance: number;  // 0-1, chance of suboptimal move

  // Target selection priority
  targeting: 'nearest' | 'weakest' | 'strongest' | 'healer' | 'caster' | 'random';

  // Combat style
  style: 'aggressive' | 'defensive' | 'tactical' | 'cowardly' | 'berserk';

  // Positioning preference
  positioning: 'melee' | 'ranged' | 'flanking' | 'support';

  // Special behaviors (all optional)
  usesRangedIfAvailable?: boolean;  // Prefers ranged attacks when possible
  protectsAllies?: boolean;         // Will defend low-HP allies
  fleeThreshold?: number;           // HP% to start fleeing (0.25 = 25%)
  callsForHelp?: boolean;           // Alerts nearby mobs when hurt
  focusesSameTarget?: boolean;      // Pack tactics - all attack same target
  usesCover?: boolean;              // Seeks cover when available
  savesAbilitiesForCrits?: boolean; // Holds special abilities for opportunities

  // Intelligence-based behaviors
  recognizesThreats?: boolean;      // INT 8+ - identifies dangerous targets
  usesTerrain?: boolean;            // INT 10+ - uses environment tactically
  coordinatesWithPack?: boolean;    // INT 8+ - works with allies
  anticipatesSpells?: boolean;      // INT 12+ - spreads out, interrupts casters
}

/**
 * Full mob state for combat
 */
export interface Mob {
  id: string;
  name: string;
  displayName?: string; // "Goblin 1", "Goblin 2" for multiples

  // Combat stats
  ac: number;
  maxHp: number;
  currentHp: number;
  tempHp: number;

  // Death state (for visual display - mob stays on board when dead)
  dead: boolean;

  // Abilities (for saves, checks)
  strength: number;
  dexterity: number;
  constitution: number;
  intelligence: number;
  wisdom: number;
  charisma: number;

  // Saves (if different from ability mod)
  saves: {
    strength?: number;
    dexterity?: number;
    constitution?: number;
    intelligence?: number;
    wisdom?: number;
    charisma?: number;
  };

  // Combat data
  actions: MobAction[];
  speed: number;
  conditions: MobCondition[];

  // Tactical AI
  tactics: MobTactics;

  // Resistances/immunities (comma-separated from SRD)
  damageResistances: string;
  damageImmunities: string;
  damageVulnerabilities: string;
  conditionImmunities: string;

  // Attention table — who this mob is focused on
  attentionTable: Record<string, AttentionEntry>;

  // GM behavior notes - natural language instructions for AI
  // Examples:
  //   "Cowardly - flees at 50% HP, begs for mercy if cornered"
  //   "Protects the shaman at all costs"
  //   "Uses hit-and-run tactics, never stands still"
  behaviorNotes?: string;

  // Metadata
  cr: number;
  size: string;
  type: string;
  alignment: string;
  open5eSlug?: string; // If imported from SRD
  bestiaryId?: string | null; // Source bestiary template (null for ad-hoc mobs)
  /** Persisted per-instance iso sprites — seeds the iso cache on load/realtime. */
  isoSprites?: { south?: string; north?: string; east?: string; west?: string } | null;

  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Params for creating a mob manually
 */
export interface CreateMobParams {
  name: string;
  ac: number;
  maxHp: number;
  strength?: number;
  dexterity?: number;
  constitution?: number;
  intelligence?: number;
  wisdom?: number;
  charisma?: number;
  speed?: number;
  actions?: MobAction[];
  cr?: number;
  size?: string;
  type?: string;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Calculate ability modifier from score
 */
export function getAbilityMod(score: number): number {
  return Math.floor((score - 10) / 2);
}

/**
 * Infer tactical preferences from creature stats and type
 */
export function inferTactics(
  intelligence: number,
  creatureType: string,
  actions: MobAction[],
  alignment: string = ''
): MobTactics {
  const hasRanged = actions.some((a) => a.range === 'ranged');
  const hasMelee = actions.some((a) => a.range === 'melee');
  const isEvil = alignment.toLowerCase().includes('evil');
  const isChaotic = alignment.toLowerCase().includes('chaotic');

  // Base tactics by creature type
  const typeDefaults: Record<string, Partial<MobTactics>> = {
    beast: { targeting: 'weakest', style: 'aggressive', positioning: 'melee' },
    humanoid: { targeting: 'nearest', style: 'tactical', positioning: 'melee' },
    undead: { targeting: 'nearest', style: 'berserk', positioning: 'melee' },
    fiend: { targeting: 'healer', style: 'aggressive', positioning: 'melee' },
    dragon: { targeting: 'strongest', style: 'tactical', positioning: 'ranged' },
    giant: { targeting: 'nearest', style: 'aggressive', positioning: 'melee' },
    aberration: { targeting: 'caster', style: 'tactical', positioning: 'ranged' },
    monstrosity: { targeting: 'weakest', style: 'aggressive', positioning: 'melee' },
    construct: { targeting: 'nearest', style: 'defensive', positioning: 'melee' },
    elemental: { targeting: 'nearest', style: 'berserk', positioning: 'melee' },
    fey: { targeting: 'caster', style: 'tactical', positioning: 'flanking' },
    celestial: { targeting: 'strongest', style: 'defensive', positioning: 'support' },
    ooze: { targeting: 'nearest', style: 'berserk', positioning: 'melee' },
    plant: { targeting: 'nearest', style: 'defensive', positioning: 'melee' },
  };

  const typeKey = creatureType.toLowerCase();
  const baseStyle = typeDefaults[typeKey] || {
    targeting: 'nearest' as const,
    style: 'aggressive' as const,
    positioning: 'melee' as const,
  };

  // Adjust positioning based on available actions
  let positioning = baseStyle.positioning || 'melee';
  if (hasRanged && !hasMelee) {
    positioning = 'ranged';
  } else if (hasRanged && hasMelee) {
    positioning = intelligence >= 10 ? 'tactical' as any : 'melee';
    // Actually keep ranged preference for smart creatures
    if (intelligence >= 10) positioning = 'ranged';
  }

  // Calculate IQ tier
  const iq = getMobIQTier(intelligence);
  const mistakeChance = IQ_MISTAKE_CHANCE[iq];

  // Build tactics object
  const tactics: MobTactics = {
    iq,
    mistakeChance,

    targeting: baseStyle.targeting || 'nearest',
    style: baseStyle.style || 'aggressive',
    positioning: positioning as any,

    // Intelligence-based behaviors
    usesRangedIfAvailable: hasRanged && intelligence >= 6,
    protectsAllies: intelligence >= 8 && !isChaotic,
    fleeThreshold: intelligence >= 6 ? (isEvil ? 0.15 : 0.25) : undefined,
    callsForHelp: intelligence >= 8,
    focusesSameTarget: intelligence >= 8,
    usesCover: hasRanged && intelligence >= 10,
    savesAbilitiesForCrits: intelligence >= 12,

    // Advanced tactics (INT-gated)
    recognizesThreats: intelligence >= 8,
    usesTerrain: intelligence >= 10,
    coordinatesWithPack: intelligence >= 8,
    anticipatesSpells: intelligence >= 12,
  };

  // Cowardly creatures (low WIS or certain types)
  if (creatureType.toLowerCase() === 'goblinoid' || (intelligence >= 6 && intelligence <= 8)) {
    tactics.fleeThreshold = 0.3; // Flee at 30% HP
    tactics.style = 'cowardly';
  }

  // Berserker override for undead/oozes (no self-preservation)
  if (['undead', 'ooze', 'construct'].includes(typeKey)) {
    tactics.fleeThreshold = undefined;
    tactics.style = 'berserk';
  }

  return tactics;
}

/**
 * Convert Open5eMonster to Mob
 */
export function mobFromOpen5e(monster: Open5eMonster, instanceNumber?: number): Mob {
  const now = new Date();

  // Parse all actions
  const actions: MobAction[] = [];
  for (const action of monster.actions) {
    const parsed = parseOpen5eAction(action);
    if (parsed) actions.push(parsed);
  }

  // Fall back to a basic attack if no parsed actions
  if (actions.length === 0) {
    actions.push({
      name: 'Attack',
      attackBonus: getAbilityMod(monster.strength) + 2, // Assume +2 prof
      damageDice: `1d6+${getAbilityMod(monster.strength)}`,
      damageType: 'bludgeoning',
      range: 'melee',
    });
  }

  // Infer tactical preferences from stats
  const tactics = inferTactics(
    monster.intelligence,
    monster.type,
    actions,
    monster.alignment
  );

  return {
    id: crypto.randomUUID(),
    name: monster.name,
    displayName: instanceNumber ? `${monster.name} ${instanceNumber}` : monster.name,

    ac: monster.armor_class,
    maxHp: monster.hit_points,
    currentHp: monster.hit_points,
    tempHp: 0,
    dead: false,

    strength: monster.strength,
    dexterity: monster.dexterity,
    constitution: monster.constitution,
    intelligence: monster.intelligence,
    wisdom: monster.wisdom,
    charisma: monster.charisma,

    saves: {
      strength: monster.strength_save ?? undefined,
      dexterity: monster.dexterity_save ?? undefined,
      constitution: monster.constitution_save ?? undefined,
      intelligence: monster.intelligence_save ?? undefined,
      wisdom: monster.wisdom_save ?? undefined,
      charisma: monster.charisma_save ?? undefined,
    },

    actions,
    speed: monster.speed.walk || 30,
    conditions: [],
    tactics,

    damageResistances: monster.damage_resistances,
    damageImmunities: monster.damage_immunities,
    damageVulnerabilities: monster.damage_vulnerabilities,
    conditionImmunities: monster.condition_immunities,

    attentionTable: {},

    cr: monster.cr,
    size: monster.size,
    type: monster.type,
    alignment: monster.alignment,
    open5eSlug: monster.slug,

    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Create a mob from manual params
 */
export function createMob(params: CreateMobParams): Mob {
  const now = new Date();
  const intelligence = params.intelligence ?? 10;
  const type = params.type ?? 'humanoid';

  const actions: MobAction[] = params.actions ?? [{
    name: 'Attack',
    attackBonus: getAbilityMod(params.strength ?? 10) + 2,
    damageDice: `1d6+${getAbilityMod(params.strength ?? 10)}`,
    damageType: 'bludgeoning',
    range: 'melee',
  }];

  // Infer tactics from stats
  const tactics = inferTactics(intelligence, type, actions);

  return {
    id: crypto.randomUUID(),
    name: params.name,
    displayName: params.name,

    ac: params.ac,
    maxHp: params.maxHp,
    currentHp: params.maxHp,
    tempHp: 0,
    dead: false,

    strength: params.strength ?? 10,
    dexterity: params.dexterity ?? 10,
    constitution: params.constitution ?? 10,
    intelligence,
    wisdom: params.wisdom ?? 10,
    charisma: params.charisma ?? 10,

    saves: {},

    actions,
    speed: params.speed ?? 30,
    conditions: [],
    tactics,

    damageResistances: '',
    damageImmunities: '',
    damageVulnerabilities: '',
    conditionImmunities: '',

    attentionTable: {},

    cr: params.cr ?? 0,
    size: params.size ?? 'Medium',
    type,
    alignment: 'neutral',

    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Parse a mob's three affinity strings into the structured form core understands.
 *
 * `Mob` stores these as comma-separated strings because that is what the SRD
 * import produced and what `dbToMob` joins the DB's `string[]` columns into.
 * `parseAffinityList` accepts either shape, so the storage shape is left alone
 * in this pass and the parsing happens here, at the point of use.
 */
function mobAffinity(mob: Mob): DamageAffinity {
  return {
    immune: parseAffinityList(mob.damageImmunities),
    resistant: parseAffinityList(mob.damageResistances),
    vulnerable: parseAffinityList(mob.damageVulnerabilities),
  };
}

/**
 * Check if mob resists/is immune/vulnerable to damage type.
 *
 * This used to be `mob.damageResistances.toLowerCase().includes(type)`, which
 * was wrong twice: `"piercing"` matched inside longer phrases, and the SRD
 * string "bludgeoning, piercing, and slashing from nonmagical attacks" matched
 * unconditionally, so a +1 longsword got resisted by a creature the rules say
 * it hurts in full. Core's `parseAffinityList` / `affinityVerdict` do exact
 * token matching and honour the nonmagical qualifier.
 *
 * @param magical whether the incoming damage came from a magical source. Only
 *   consulted by `nonmagicalOnly` entries. Defaults to `false` because the
 *   battle sim does not yet track whether a weapon is magical — so a +1 weapon
 *   is still treated as nonmagical here until the sim carries that flag. That
 *   is the same answer the old code gave, and strictly narrower everywhere else.
 */
export function getDamageMultiplier(mob: Mob, damageType: string, magical = false): number {
  return affinityMultiplier(
    affinityVerdict(mobAffinity(mob), { type: damageType, magical }),
  );
}

// =============================================================================
// STORE
// =============================================================================

// =============================================================================
// DB PERSISTENCE HELPERS
// =============================================================================

interface DbEncounterMob {
  id: string;
  campaign_id: string;
  bestiary_id: string | null;
  display_name: string;
  current_hp: number;
  max_hp: number;
  temp_hp: number;
  ac: number;
  dead: boolean;
  conditions: unknown[];
  attention: Record<string, unknown>;
  abilities: Record<string, number>;
  saves: Record<string, number>;
  actions: unknown[];
  tactics: Record<string, unknown>;
  speed: number;
  resistances: string[];
  immunities: string[];
  vulnerabilities: string[];
  condition_immunities: string[];
  img_url: string | null;
  iso_sprites: { south?: string; north?: string; east?: string; west?: string } | null;
  notes: string | null;
  is_named: boolean;
  cr: string | null;
  size: string;
  creature_type: string | null;
  base_name: string | null;
  open5e_slug: string | null;
  created_at: string;
  updated_at: string;
}

function dbToMob(db: DbEncounterMob): Mob {
  // The DB row may arrive as snake_case (raw DB) OR camelCase (via tRPC dbToEncounterMob).
  // Handle both shapes to be safe.
  const camel = db as unknown as Record<string, unknown>;
  const abilities = db.abilities ?? (camel.abilities as Record<string, number>) ?? { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 };
  const intelligence = abilities.int ?? 10;
  const actions = ((db.actions ?? (camel.actions as unknown[]) ?? []) as MobAction[]);
  const type = db.creature_type ?? (camel.creatureType as string) ?? 'humanoid';
  const displayName = db.display_name ?? (camel.displayName as string) ?? 'Creature';
  const baseName = db.base_name ?? (camel.baseName as string) ?? displayName;

  return {
    id: db.id ?? (camel.id as string),
    name: baseName,
    displayName,
    ac: db.ac ?? (camel.ac as number) ?? 10,
    maxHp: db.max_hp ?? (camel.maxHp as number) ?? 1,
    currentHp: db.current_hp ?? (camel.currentHp as number) ?? db.max_hp ?? (camel.maxHp as number) ?? 1,
    tempHp: db.temp_hp ?? (camel.tempHp as number) ?? 0,
    dead: db.dead ?? (camel.dead as boolean) ?? false,
    strength: abilities.str ?? 10,
    dexterity: abilities.dex ?? 10,
    constitution: abilities.con ?? 10,
    intelligence,
    wisdom: abilities.wis ?? 10,
    charisma: abilities.cha ?? 10,
    saves: {
      strength: (db.saves as Record<string, number>)?.str ?? undefined,
      dexterity: (db.saves as Record<string, number>)?.dex ?? undefined,
      constitution: (db.saves as Record<string, number>)?.con ?? undefined,
      intelligence: (db.saves as Record<string, number>)?.int ?? undefined,
      wisdom: (db.saves as Record<string, number>)?.wis ?? undefined,
      charisma: (db.saves as Record<string, number>)?.cha ?? undefined,
    },
    actions,
    speed: db.speed ?? (camel.speed as number) ?? 30,
    conditions: (db.conditions ?? (camel.conditions as unknown[]) ?? []) as MobCondition[],
    tactics: (db.tactics as unknown as MobTactics) ?? (camel.tactics as unknown as MobTactics) ?? inferTactics(intelligence, type, actions),
    damageResistances: (db.resistances ?? (camel.resistances as string[]) ?? []).join(', '),
    damageImmunities: (db.immunities ?? (camel.immunities as string[]) ?? []).join(', '),
    damageVulnerabilities: (db.vulnerabilities ?? (camel.vulnerabilities as string[]) ?? []).join(', '),
    conditionImmunities: (db.condition_immunities ?? (camel.conditionImmunities as string[]) ?? []).join(', '),
    attentionTable: (db.attention ?? (camel.attention as Record<string, unknown>) ?? {}) as Record<string, AttentionEntry>,
    cr: parseCr(db.cr ?? (camel.cr as string)),
    size: db.size ?? (camel.size as string) ?? 'Medium',
    type,
    alignment: '',
    open5eSlug: db.open5e_slug ?? (camel.open5eSlug as string) ?? undefined,
    bestiaryId: (db.bestiary_id as string | null | undefined) ?? (camel.bestiaryId as string | null | undefined) ?? null,
    isoSprites: (db.iso_sprites as Mob['isoSprites']) ?? (camel.isoSprites as Mob['isoSprites']) ?? null,
    createdAt: new Date(db.created_at ?? (camel.createdAt as string) ?? Date.now()),
    updatedAt: new Date(db.updated_at ?? (camel.updatedAt as string) ?? Date.now()),
  };
}

/** Seed iso sprite cache from a mob's persisted isoSprites URLs, falling
 *  back to the bestiary template when the mob row itself has no sprites.
 *  Ad-hoc mobs save to the mob row; bestiary-spawned mobs save to the
 *  template — so on refresh we need to consult both. */
function seedIsoSpritesFromMob(mob: Mob): void {
  void (async () => {
    const { useIsoSpriteStore } = await import('./isoSpriteStore');
    const isoStore = useIsoSpriteStore.getState();
    const facings: Array<'south' | 'north' | 'east' | 'west'> = ['south', 'north', 'east', 'west'];

    // Prefer per-mob override when present.
    const own = mob.isoSprites;
    if (own && facings.some((f) => own[f])) {
      for (const f of facings) {
        const url = own[f];
        if (url) isoStore.setCustomSprite(mob.id, url, f, mob.displayName || mob.name);
      }
      return;
    }

    // Fall back to the bestiary template — sprites generated for a spawned
    // mob auto-persist to its parent template, so every future spawn from
    // that template gets the sprites too.
    if (!mob.bestiaryId) return;
    const { useBestiaryStore } = await import('./bestiaryStore');
    const template = useBestiaryStore.getState().entries.find((e) => e.id === mob.bestiaryId);
    const templateSprites = template?.isoSprites;
    if (!templateSprites) return;
    for (const f of facings) {
      const url = templateSprites[f];
      if (url) isoStore.setCustomSprite(mob.id, url, f, mob.displayName || mob.name);
    }
  })().catch((err) => console.warn('[mobStore] iso seed failed:', err));
}

function parseCr(cr: string | null | undefined): number {
  if (!cr) return 0;
  if (cr.includes('/')) {
    const [n, d] = cr.split('/').map(Number);
    return d ? n / d : 0;
  }
  return parseFloat(cr) || 0;
}

/** Monotonic counter for mob token grid positioning — prevents concurrent spawnMob
 *  calls from reading the same existingMobTokenCount and stacking tokens. */
/**
 * Push every monster's HP tier, HP and conditions onto its tokens.
 *
 * HP changes reach tokens through syncFromCombatant, which is local only:
 * the token rows keep whatever HP bar they were saved with. After a reload
 * the bars showed that stale state until the monster was hit again. Called
 * after monsters load and after a scene's tokens load, whichever is last.
 */
export function syncTokensFromMobs(): void {
  const tokenStore = useTokenStore.getState();
  for (const mob of useMobStore.getState().mobs.values()) {
    const hpTier: HpTier = mob.dead ? 'dead' : getHpTier(mob.currentHp, mob.maxHp);
    tokenStore.syncFromCombatant(mob.id, {
      currentHp: mob.currentHp,
      maxHp: mob.maxHp,
      hpTier,
      conditions: mob.conditions.map((c) => ({
        type: c.type,
        source: c.sourceId,
        duration: c.duration ?? null,
        saveEndOfTurn: false,
      })),
    });
  }
}

let spawnPositionCounter = 0;

/** Debounced DB sync — batches rapid mutations.
 * Reads fresh state at flush time to avoid stale closure bugs. */
const pendingSyncs = new Map<string, ReturnType<typeof setTimeout>>();
/** Tracks which fields are dirty per mob so we always sync latest state */
const dirtyFields = new Map<string, Set<string>>();

function debouncedDbSync(mobId: string, fields: Record<string, unknown>) {
  // Mark fields as dirty and store latest values isn't needed —
  // we read fresh from store at flush time
  let dirty = dirtyFields.get(mobId);
  if (!dirty) {
    dirty = new Set();
    dirtyFields.set(mobId, dirty);
  }
  for (const key of Object.keys(fields)) {
    dirty.add(key);
  }

  const existing = pendingSyncs.get(mobId);
  if (existing) clearTimeout(existing);
  pendingSyncs.set(mobId, setTimeout(async () => {
    pendingSyncs.delete(mobId);
    const fieldNames = dirtyFields.get(mobId);
    dirtyFields.delete(mobId);
    if (!fieldNames || fieldNames.size === 0) return;

    // Monster rows are GM-owned (updateMob is a dmProcedure). A player's
    // client sees the same local changes, but its save is refused with 403,
    // and the retry below then looped forever. Only the GM persists.
    if (!isCurrentUserGM()) return;

    // Read fresh state at flush time — never use stale closures
    const mob = useMobStore.getState().mobs.get(mobId);
    if (!mob) return;

    const updates: Record<string, unknown> = {};
    for (const field of fieldNames) {
      if (field in mob) {
        updates[field] = (mob as unknown as Record<string, unknown>)[field];
      }
    }

    // persistWithRetry retries once with backoff and surfaces a sticky toast on
    // final failure — so a failed HP/condition/temp-HP write is NEVER silent.
    // (trpc.mutate throws on error, which persistWithRetry treats as a failure.)
    const { ok } = await persistWithRetry(
      'monster HP/conditions',
      () => trpc.bestiary.updateMob.mutate({ id: mobId, ...updates }),
      {
        onFinalFailure: (err) => {
          // A refusal won't succeed on retry; stop instead of looping.
          const code = (err as { data?: { code?: string } } | null)?.data?.code;
          if (code === 'FORBIDDEN' || code === 'UNAUTHORIZED') return;
          // Re-mark the fields dirty and reschedule the debounced sync so the
          // write actually retries again, rather than waiting for a future
          // mutation that may never come.
          let retryDirty = dirtyFields.get(mobId);
          if (!retryDirty) {
            retryDirty = new Set();
            dirtyFields.set(mobId, retryDirty);
          }
          for (const field of fieldNames) {
            retryDirty.add(field);
          }
          // Only reschedule if no newer sync is already pending for this mob,
          // and the mob still exists locally — avoids stacking timers / churn.
          if (!pendingSyncs.has(mobId) && useMobStore.getState().mobs.has(mobId)) {
            const reMob = useMobStore.getState().mobs.get(mobId);
            if (reMob) {
              const retryFields: Record<string, unknown> = {};
              for (const field of retryDirty) {
                retryFields[field] = (reMob as unknown as Record<string, unknown>)[field];
              }
              debouncedDbSync(mobId, retryFields);
            }
          }
        },
      },
    );
    if (ok) {
      // Clear any stale dirty marks left over from a prior failed attempt now
      // that the latest state has been persisted successfully.
      dirtyFields.delete(mobId);
    }
  }, 300));
}

/** Version counter per mob — incremented on local mutations, used to ignore stale realtime updates */
const localVersions = new Map<string, number>();
function bumpLocalVersion(mobId: string): number {
  const v = (localVersions.get(mobId) || 0) + 1;
  localVersions.set(mobId, v);
  return v;
}

interface MobState {
  // Data
  mobs: Map<string, Mob>;

  // Instance counters for duplicate names
  instanceCounters: Map<string, number>;

  // Campaign context for DB operations
  campaignId: string | null;

  // Realtime
  realtimeCleanup: (() => void) | null;

  // Actions - CRUD
  addMob: (params: CreateMobParams) => Mob;
  addMobFromSrd: (monster: Open5eMonster) => Mob;
  removeMob: (id: string, opts?: { skipUndo?: boolean }) => Promise<void>;
  /** Restore a mob (and its token, if provided) — used by the undo system.
   *  Upserts the mob row to DB preserving the original id. */
  restoreMob: (mob: Mob, token?: Token | null, sceneId?: string | null) => Promise<void>;
  removeAllMobs: () => Promise<void>;
  archiveDeadMobs: () => Promise<{ archivedCount: number; archivedNames: string[] }>;
  updateMob: (id: string, updates: Partial<Mob>) => void;

  // Actions - DB-backed spawning
  spawnFromBestiary: (bestiaryId: string, count: number, campaignId: string) => Promise<Mob[]>;
  spawnAdHoc: (params: CreateMobParams & { campaignId: string }) => Promise<Mob>;

  // Actions - Combat
  damageMob: (id: string, amount: number, attackerId?: string, damageType?: string) => {
    finalDamage: number;
    newHp: number;
    died: boolean;
  } | null;
  /**
   * Apply damage that has ALREADY been through the target's affinity.
   *
   * The second entry point exists because the battle sim now asks the game
   * system's adapter what a hit does — and the adapter halves resisted damage
   * itself. Handing that number to `damageMob` would halve it a second time, so
   * a resistant monster took a quarter. A boolean flag on `damageMob` would say
   * the same thing in a worse place; two named doors say which question the
   * caller has already answered.
   */
  applyResolvedDamage: (id: string, finalDamage: number, attackerId?: string) => {
    finalDamage: number;
    newHp: number;
    died: boolean;
  } | null;
  healMob: (id: string, amount: number) => void;
  setTempHp: (id: string, amount: number) => void;

  // Actions - Conditions
  applyCondition: (id: string, condition: ConditionType, sourceId?: string, duration?: number) => void;
  removeCondition: (id: string, conditionType: ConditionType) => void;
  tickConditions: (id: string) => void; // Reduce durations by 1

  // Actions - Attention
  updateAttention: (
    mobId: string,
    targetId: string,
    action: { damage?: number; healing?: number; taunt?: boolean; isMelee?: boolean }
  ) => void;
  clearAttention: (mobId: string) => void;

  // Actions - DB Persistence
  loadMobs: (campaignId: string, sceneId?: string) => Promise<void>;
  clearEncounter: (campaignId: string) => Promise<void>;
  subscribeToRealtime: (campaignId: string) => void;
  unsubscribeFromRealtime: () => void;

  // Queries
  getMob: (id: string) => Mob | undefined;
  getMobsByName: (name: string) => Mob[];
  getAliveMobs: () => Mob[];
  getMobHpTier: (id: string) => HpTier | undefined;
  getTopTarget: (mobId: string) => string | undefined; // Highest attention target

  // Unified mob pipeline — single entry point for all mob creation
  spawnMob: (params: SpawnMobParams, options?: SpawnMobOptions) => Promise<SpawnResult>;

  // Campaign isolation — clear in-memory state without touching DB
  clearLocalState: () => void;
}

// =============================================================================
// SPAWN MOB PIPELINE TYPES
// =============================================================================

export interface SpawnMobParams {
  campaignId: string;
  sceneId?: string;
  bestiaryId?: string;
  // Ad-hoc fields (used if no bestiaryId)
  name: string;
  ac: number;
  maxHp: number;
  abilities?: { str: number; dex: number; con: number; int: number; wis: number; cha: number };
  actions?: MobAction[];
  resistances?: string[];
  immunities?: string[];
  conditionImmunities?: string[];
  size?: string;
  cr?: number;
  speed?: number;
  type?: string;
  displayName?: string;
  open5eSlug?: string;
  imgUrl?: string;
}

export interface SpawnMobOptions {
  position?: { x: number; y: number };
  rollInitiative?: boolean;
  imageUrl?: string;
  count?: number;
}

export interface SpawnResult {
  mobs: Mob[];
  tokens: Token[];
}

// Attention calculation constants (from ai-types.ts)
const ATTENTION_MODIFIERS = {
  DAMAGE_PER_POINT: 1.0,
  HEAL_PER_POINT: 0.5,
  TAUNT: 50,
  MELEE_MULTIPLIER: 1.1,
  GRUDGE_DAMAGE_RATIO: 0.3,
};

export const useMobStore = create<MobState>((set, get) => ({
  mobs: new Map(),
  instanceCounters: new Map(),
  campaignId: null,
  realtimeCleanup: null,

  // ===========================================================================
  // CRUD
  // ===========================================================================

  addMob: (params) => {
    // Track instance number for duplicate names
    const counters = get().instanceCounters;
    const currentCount = counters.get(params.name) || 0;
    const instanceNumber = currentCount + 1;

    const mob = createMob(params);
    // Set display name with instance number if this is a duplicate
    mob.displayName = instanceNumber > 1 ? `${params.name} ${instanceNumber}` : params.name;

    set((state) => {
      const newMobs = new Map(state.mobs);
      newMobs.set(mob.id, mob);

      const newCounters = new Map(state.instanceCounters);
      newCounters.set(params.name, instanceNumber);

      return { mobs: newMobs, instanceCounters: newCounters };
    });

    return mob;
  },

  addMobFromSrd: (monster) => {
    // Track instance number for duplicate names
    const counters = get().instanceCounters;
    const currentCount = counters.get(monster.name) || 0;
    const instanceNumber = currentCount + 1;

    const mob = mobFromOpen5e(monster, instanceNumber > 1 ? instanceNumber : undefined);

    set((state) => {
      const newMobs = new Map(state.mobs);
      newMobs.set(mob.id, mob);

      const newCounters = new Map(state.instanceCounters);
      newCounters.set(monster.name, instanceNumber);

      return { mobs: newMobs, instanceCounters: newCounters };
    });

    return mob;
  },

  removeMob: async (id, opts) => {
    // Snapshot for undo BEFORE we touch anything.
    const snapshotMob = get().mobs.get(id);
    const tokenStore = useTokenStore.getState();
    const sceneId = tokenStore.currentSceneId;
    const snapshotTokens: Token[] = [];
    for (const token of tokenStore.tokens.values()) {
      if (token.combatantId === id) snapshotTokens.push({ ...token });
    }

    // Remove associated token(s). Don't break after the first — if a mob has
    // accumulated duplicate tokens (rare but seen in orphan-token scenarios),
    // they all need to go.
    for (const t of snapshotTokens) tokenStore.removeToken(t.id);

    // Remove from initiative
    useInitiativeStore.getState().removeEntry(id);

    // Remove from local state
    set((state) => {
      const newMobs = new Map(state.mobs);
      newMobs.delete(id);
      return { mobs: newMobs };
    });

    // Delete from DB
    try {
      await trpc.bestiary.removeMob.mutate({ id });
    } catch (err) {
      console.warn('[mobStore] removeMob DB delete failed (may already be gone):', err);
    }

    // Surface undo pill (single-mob delete). Skipped when an upstream caller
    // (e.g. tokenStore.removeSelected) is already managing a bulk snapshot.
    if (snapshotMob && !opts?.skipUndo) {
      const restore = get().restoreMob;
      const firstToken = snapshotTokens[0] ?? null;
      useUndoStore.getState().pushDelete({
        type: 'mob:remove',
        description: `Removed ${snapshotMob.displayName ?? snapshotMob.name}`,
        undo: async () => {
          await restore(snapshotMob, firstToken, sceneId);
          for (let i = 1; i < snapshotTokens.length; i++) {
            useTokenStore.getState().restoreToken(snapshotTokens[i]);
          }
        },
        redo: async () => {
          await get().removeMob(snapshotMob.id);
        },
      });
    }
  },

  restoreMob: async (mob, token, sceneId) => {
    // 1. DB upsert into machina_encounter_mobs preserving original id.
    //    Mirrors the column shape used elsewhere (snake_case).
    const row = {
      id: mob.id,
      campaign_id: get().campaignId,
      bestiary_id: mob.bestiaryId ?? null,
      scene_id: sceneId ?? null,
      display_name: mob.displayName ?? mob.name,
      base_name: mob.name,
      current_hp: mob.currentHp,
      max_hp: mob.maxHp,
      temp_hp: mob.tempHp,
      ac: mob.ac,
      dead: mob.dead,
      conditions: mob.conditions,
      attention: mob.attentionTable,
      abilities: {
        str: mob.strength,
        dex: mob.dexterity,
        con: mob.constitution,
        int: mob.intelligence,
        wis: mob.wisdom,
        cha: mob.charisma,
      },
      saves: {
        ...(mob.saves.strength !== undefined && { str: mob.saves.strength }),
        ...(mob.saves.dexterity !== undefined && { dex: mob.saves.dexterity }),
        ...(mob.saves.constitution !== undefined && { con: mob.saves.constitution }),
        ...(mob.saves.intelligence !== undefined && { int: mob.saves.intelligence }),
        ...(mob.saves.wisdom !== undefined && { wis: mob.saves.wisdom }),
        ...(mob.saves.charisma !== undefined && { cha: mob.saves.charisma }),
      },
      actions: mob.actions,
      tactics: mob.tactics,
      speed: mob.speed,
      resistances: mob.damageResistances ? mob.damageResistances.split(',').map((s) => s.trim()).filter(Boolean) : [],
      immunities: mob.damageImmunities ? mob.damageImmunities.split(',').map((s) => s.trim()).filter(Boolean) : [],
      vulnerabilities: mob.damageVulnerabilities ? mob.damageVulnerabilities.split(',').map((s) => s.trim()).filter(Boolean) : [],
      condition_immunities: mob.conditionImmunities ? mob.conditionImmunities.split(',').map((s) => s.trim()).filter(Boolean) : [],
      img_url: null,
      iso_sprites: mob.isoSprites ?? null,
      notes: null,
      is_named: false,
      cr: mob.cr ? String(mob.cr) : null,
      size: mob.size,
      creature_type: mob.type,
      open5e_slug: mob.open5eSlug ?? null,
    };

    try {
      const { error } = await supabase
        .from('machina_encounter_mobs')
        .upsert(row, { onConflict: 'id' });
      if (error) throw error;
    } catch (err) {
      console.error('[mobStore] restoreMob DB upsert failed:', err);
      // Continue with local restore even if DB write failed — user gets the
      // visual back, but it'll be local-only until next manual sync.
    }

    // 2. Restore in local Map.
    set((state) => {
      const newMobs = new Map(state.mobs);
      newMobs.set(mob.id, { ...mob, updatedAt: new Date() });
      return { mobs: newMobs };
    });

    // 3. Restore the linked token (caller provides it from snapshot).
    if (token) {
      useTokenStore.getState().restoreToken(token);
    }
  },

  removeAllMobs: async () => {
    const { campaignId } = get();

    // Remove ALL non-character tokens from the board — this catches orphans
    // (tokens whose backing mob row was already gone or never persisted).
    // Import dynamically to avoid a circular dep with characterStore.
    const tokenStore = useTokenStore.getState();
    const { useCharacterStore } = await import('./characterStore');
    const characterIds = new Set(useCharacterStore.getState().characters.keys());

    for (const [tokenId, token] of tokenStore.tokens) {
      // Preserve PC tokens; nuke every other token on the board.
      if (token.combatantId && characterIds.has(token.combatantId)) continue;
      tokenStore.removeToken(tokenId);
    }

    // Clear local mob state. Leave initiative + combat_state alone — the GM
    // ends combat explicitly via Init Panel "End Combat". This prevents any
    // code path that calls removeAllMobs from nuking initiative unexpectedly.
    set({ mobs: new Map(), instanceCounters: new Map() });
    spawnPositionCounter = 0;

    // Clear from DB
    if (campaignId) {
      try {
        await trpc.bestiary.clearEncounter.mutate({ campaignId });
      } catch (err) {
        console.warn('[mobStore] removeAllMobs DB clear failed:', err);
      }

      // Strip the now-rowless mob participants from the encounter/initiative.
      // The local mob Map is already empty (above), so reconcile drops every
      // mob participant that no longer has a backing row — keeping characters.
      // Without this, removeAllMobs left combat_state.participants intact,
      // producing the "No monsters in encounter" panel beside a full
      // Initiative list (mob rows gone, participant blob stale).
      const { useEncounterStore } = await import('./encounterStore');
      await useEncounterStore.getState().reconcileParticipants(campaignId);
    }
  },

  archiveDeadMobs: async () => {
    const { mobs } = get();
    const deadMobs = Array.from(mobs.values()).filter(m => m.dead);
    const archivedNames = deadMobs.map(m => m.displayName || m.name);

    // Remove dead mob tokens + initiative entries
    const tokenStore = useTokenStore.getState();
    const initStore = useInitiativeStore.getState();
    for (const mob of deadMobs) {
      initStore.removeEntry(mob.id);
      for (const [tokenId, token] of tokenStore.tokens) {
        if (token.combatantId === mob.id) {
          tokenStore.removeToken(tokenId);
          break;
        }
      }
    }

    // Remove dead mobs from local state
    set((state) => {
      const newMobs = new Map(state.mobs);
      for (const mob of deadMobs) {
        newMobs.delete(mob.id);
      }
      return { mobs: newMobs };
    });

    // Delete dead mobs from DB
    for (const mob of deadMobs) {
      try {
        await trpc.bestiary.removeMob.mutate({ id: mob.id });
      } catch (err) {
        console.warn('[mobStore] archiveDeadMobs DB delete failed for', mob.id, err);
      }
    }

    return { archivedCount: deadMobs.length, archivedNames };
  },

  updateMob: (id, updates) => {
    set((state) => {
      const mob = state.mobs.get(id);
      if (!mob) return state;

      const updated: Mob = {
        ...mob,
        ...updates,
        updatedAt: new Date(),
      };

      const newMobs = new Map(state.mobs);
      newMobs.set(id, updated);
      return { mobs: newMobs };
    });
  },

  // ===========================================================================
  // COMBAT
  // ===========================================================================

  /**
   * Damage a monster, affinity and all.
   *
   * This is the door for a caller holding RAW damage — a manual GM entry, a
   * spell card, a chat command. It works out the multiplier and then hands over
   * to `applyResolvedDamage`, which owns everything downstream: temp HP, the HP
   * floor, death, the attention table, the token's HP tier and the DB sync.
   * A caller whose damage has already been through a system adapter must use
   * that second door directly or the halving is paid twice.
   */
  damageMob: (id, amount, attackerId, damageType) => {
    const mob = get().mobs.get(id);
    if (!mob) return null;

    // Apply resistance/immunity/vulnerability
    const multiplier = damageType ? getDamageMultiplier(mob, damageType) : 1;
    return get().applyResolvedDamage(id, Math.floor(amount * multiplier), attackerId);
  },

  applyResolvedDamage: (id, finalDamage, attackerId) => {
    const mob = get().mobs.get(id);
    if (!mob) return null;

    if (finalDamage === 0) {
      return { finalDamage: 0, newHp: mob.currentHp, died: false };
    }

    // Apply to temp HP first
    let remaining = finalDamage;
    let newTempHp = mob.tempHp;
    if (newTempHp > 0) {
      const absorbed = Math.min(newTempHp, remaining);
      newTempHp -= absorbed;
      remaining -= absorbed;
    }

    const newHp = Math.max(0, mob.currentHp - remaining);
    const died = newHp === 0 && mob.currentHp > 0;

    // Update attention if we have an attacker
    let newAttentionTable = { ...mob.attentionTable };
    if (attackerId) {
      const existing = newAttentionTable[attackerId] || {
        targetId: attackerId,
        attention: 0,
        grudge: 0,
        tacticalPriority: 0,
        lastUpdated: new Date(),
      };

      const attentionDelta = finalDamage * ATTENTION_MODIFIERS.DAMAGE_PER_POINT;
      const grudgeDelta = finalDamage * ATTENTION_MODIFIERS.GRUDGE_DAMAGE_RATIO;

      newAttentionTable[attackerId] = {
        ...existing,
        attention: existing.attention + attentionDelta,
        grudge: existing.grudge + grudgeDelta,
        lastUpdated: new Date(),
      };
    }

    set((state) => {
      const newMobs = new Map(state.mobs);
      newMobs.set(id, {
        ...mob,
        currentHp: newHp,
        tempHp: newTempHp,
        dead: died ? true : mob.dead, // Mark as dead if HP hit 0
        attentionTable: newAttentionTable,
        updatedAt: new Date(),
      });
      return { mobs: newMobs };
    });

    // Sync token visuals when mob HP changes or dies
    const hpTier: HpTier = died ? 'dead' : getHpTier(newHp, mob.maxHp);
    useTokenStore.getState().syncFromCombatant(id, {
      currentHp: newHp,
      maxHp: mob.maxHp,
      hpTier,
    });

    // Persist to DB — marks fields dirty, reads fresh state at flush time
    bumpLocalVersion(id);
    debouncedDbSync(id, {
      currentHp: newHp,
      tempHp: newTempHp,
      dead: died ? true : mob.dead,
      attentionTable: newAttentionTable,
    });

    return { finalDamage, newHp, died };
  },

  healMob: (id, amount) => {
    set((state) => {
      const mob = state.mobs.get(id);
      if (!mob) return state;

      const newHp = Math.min(mob.maxHp, mob.currentHp + amount);

      const newMobs = new Map(state.mobs);
      newMobs.set(id, {
        ...mob,
        currentHp: newHp,
        updatedAt: new Date(),
      });
      return { mobs: newMobs };
    });

    // Sync token visuals (mirror what damageMob does)
    const updatedMob = get().mobs.get(id);
    if (updatedMob) {
      const hpTier = getHpTier(updatedMob.currentHp, updatedMob.maxHp);
      useTokenStore.getState().syncFromCombatant(id, {
        currentHp: updatedMob.currentHp,
        maxHp: updatedMob.maxHp,
        hpTier,
      });
      // Persist to DB
      bumpLocalVersion(id);
      debouncedDbSync(id, { currentHp: updatedMob.currentHp });
    }
  },

  setTempHp: (id, amount) => {
    let newTempHp = 0;
    set((state) => {
      const mob = state.mobs.get(id);
      if (!mob) return state;

      // Temp HP doesn't stack — take the higher value
      newTempHp = Math.max(mob.tempHp, amount);

      const newMobs = new Map(state.mobs);
      newMobs.set(id, {
        ...mob,
        tempHp: newTempHp,
        updatedAt: new Date(),
      });
      return { mobs: newMobs };
    });

    // Persist to DB
    bumpLocalVersion(id);
    debouncedDbSync(id, { tempHp: newTempHp });
  },

  // ===========================================================================
  // CONDITIONS
  // ===========================================================================

  applyCondition: (id, conditionType, sourceId, duration) => {
    set((state) => {
      const mob = state.mobs.get(id);
      if (!mob) return state;

      // Check condition immunity — split on commas to avoid substring false-matches
      const immunities = mob.conditionImmunities.toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
      if (immunities.some(imm => imm === conditionType.toLowerCase())) {
        return state; // Immune, no effect
      }

      // Check if already has this condition
      const existingIndex = mob.conditions.findIndex((c) => c.type === conditionType);
      let newConditions = [...mob.conditions];

      if (existingIndex >= 0) {
        // Update existing - refresh duration if longer
        const existing = newConditions[existingIndex];
        if (duration && (!existing.duration || duration > existing.duration)) {
          newConditions[existingIndex] = { ...existing, duration, sourceId };
        }
      } else {
        // Add new condition
        newConditions.push({ type: conditionType, sourceId, duration });
      }

      const newMobs = new Map(state.mobs);
      newMobs.set(id, {
        ...mob,
        conditions: newConditions,
        updatedAt: new Date(),
      });
      return { mobs: newMobs };
    });

    // Sync conditions to token
    const updatedMob = get().mobs.get(id);
    if (updatedMob) {
      useTokenStore.getState().syncFromCombatant(id, {
        conditions: updatedMob.conditions.map(c => ({
          type: c.type,
          source: c.sourceId,
          duration: c.duration ?? null,
          saveEndOfTurn: false,
        })),
      });
      // Persist conditions to DB
      bumpLocalVersion(id);
      debouncedDbSync(id, { conditions: updatedMob.conditions });
    }
  },

  removeCondition: (id, conditionType) => {
    set((state) => {
      const mob = state.mobs.get(id);
      if (!mob) return state;

      const newConditions = mob.conditions.filter((c) => c.type !== conditionType);

      const newMobs = new Map(state.mobs);
      newMobs.set(id, {
        ...mob,
        conditions: newConditions,
        updatedAt: new Date(),
      });
      return { mobs: newMobs };
    });

    // Sync conditions to token
    const updatedMob = get().mobs.get(id);
    if (updatedMob) {
      useTokenStore.getState().syncFromCombatant(id, {
        conditions: updatedMob.conditions.map(c => ({
          type: c.type,
          source: c.sourceId,
          duration: c.duration ?? null,
          saveEndOfTurn: false,
        })),
      });
      // Persist conditions to DB
      bumpLocalVersion(id);
      debouncedDbSync(id, { conditions: updatedMob.conditions });
    }
  },

  tickConditions: (id) => {
    set((state) => {
      const mob = state.mobs.get(id);
      if (!mob) return state;

      const newConditions = mob.conditions
        .map((c) => {
          if (c.duration === undefined) return c; // Permanent condition
          return { ...c, duration: c.duration - 1 };
        })
        .filter((c) => c.duration === undefined || c.duration > 0);

      const newMobs = new Map(state.mobs);
      newMobs.set(id, {
        ...mob,
        conditions: newConditions,
        updatedAt: new Date(),
      });
      return { mobs: newMobs };
    });
  },

  // ===========================================================================
  // ATTENTION
  // ===========================================================================

  updateAttention: (mobId, targetId, action) => {
    set((state) => {
      const mob = state.mobs.get(mobId);
      if (!mob) return state;

      const existing = mob.attentionTable[targetId] || {
        targetId,
        attention: 0,
        grudge: 0,
        tacticalPriority: 0,
        lastUpdated: new Date(),
      };

      let attentionDelta = 0;
      let grudgeDelta = 0;

      if (action.damage) {
        attentionDelta += action.damage * ATTENTION_MODIFIERS.DAMAGE_PER_POINT;
        grudgeDelta += action.damage * ATTENTION_MODIFIERS.GRUDGE_DAMAGE_RATIO;
      }
      if (action.healing) {
        attentionDelta += action.healing * ATTENTION_MODIFIERS.HEAL_PER_POINT;
      }
      if (action.taunt) {
        attentionDelta += ATTENTION_MODIFIERS.TAUNT;
      }
      if (action.isMelee) {
        attentionDelta *= ATTENTION_MODIFIERS.MELEE_MULTIPLIER;
      }

      const newAttentionTable = {
        ...mob.attentionTable,
        [targetId]: {
          ...existing,
          attention: existing.attention + attentionDelta,
          grudge: existing.grudge + grudgeDelta,
          lastUpdated: new Date(),
        },
      };

      const newMobs = new Map(state.mobs);
      newMobs.set(mobId, {
        ...mob,
        attentionTable: newAttentionTable,
        updatedAt: new Date(),
      });
      return { mobs: newMobs };
    });
  },

  clearAttention: (mobId) => {
    set((state) => {
      const mob = state.mobs.get(mobId);
      if (!mob) return state;

      const newMobs = new Map(state.mobs);
      newMobs.set(mobId, {
        ...mob,
        attentionTable: {},
        updatedAt: new Date(),
      });
      return { mobs: newMobs };
    });
  },

  // ===========================================================================
  // QUERIES
  // ===========================================================================

  getMob: (id) => get().mobs.get(id),

  getMobsByName: (name) => {
    return Array.from(get().mobs.values()).filter(
      (m) => m.name.toLowerCase() === name.toLowerCase()
    );
  },

  getAliveMobs: () => {
    return Array.from(get().mobs.values()).filter((m) => !m.dead && m.currentHp > 0);
  },

  getMobHpTier: (id) => {
    const mob = get().mobs.get(id);
    if (!mob) return undefined;
    return getHpTier(mob.currentHp, mob.maxHp);
  },

  getTopTarget: (mobId) => {
    const mob = get().mobs.get(mobId);
    if (!mob) return undefined;

    const entries = Object.values(mob.attentionTable);
    if (entries.length === 0) return undefined;

    // Sort by total attention (attention + grudge + tactical)
    entries.sort((a, b) => {
      const totalA = a.attention + a.grudge + a.tacticalPriority;
      const totalB = b.attention + b.grudge + b.tacticalPriority;
      return totalB - totalA;
    });

    return entries[0].targetId;
  },

  // ===========================================================================
  // DB PERSISTENCE
  // ===========================================================================

  loadMobs: async (campaignId, sceneId?) => {
    set({ campaignId });
    try {
      const mobs = await trpc.bestiary.listMobs.query({ campaignId, sceneId, includesDead: true });
      const mobMap = new Map<string, Mob>();
      const counters = new Map<string, number>();

      for (const em of mobs) {
        const mob = dbToMob(em as unknown as DbEncounterMob);
        mobMap.set(mob.id, mob);
        // Rebuild instance counters from existing display names
        const baseName = mob.name;
        const currentCount = counters.get(baseName) || 0;
        counters.set(baseName, currentCount + 1);
        // Seed iso sprite cache for any mob with persisted sprites
        seedIsoSpritesFromMob(mob);
      }

      set({ mobs: mobMap, instanceCounters: counters });
      console.log(`[mobStore] Loaded ${mobMap.size} mobs from DB`);
      syncTokensFromMobs();
    } catch (err) {
      console.warn('[mobStore] Failed to load mobs from DB (may be empty):', err);
    }
  },

  spawnFromBestiary: async (bestiaryId, count, campaignId) => {
    console.warn('[mobStore] spawnFromBestiary is deprecated — use spawnMob() instead');
    try {
      const spawned = await trpc.bestiary.spawn.mutate({ campaignId, bestiaryId, count });
      const newMobs: Mob[] = [];

      set((state) => {
        const newMobMap = new Map(state.mobs);
        const newCounters = new Map(state.instanceCounters);

        for (const em of spawned) {
          const mob = dbToMob(em as unknown as DbEncounterMob);
          newMobMap.set(mob.id, mob);
          newMobs.push(mob);
          const baseName = mob.name;
          const currentCount = newCounters.get(baseName) || 0;
          newCounters.set(baseName, currentCount + 1);
        }

        return { mobs: newMobMap, instanceCounters: newCounters };
      });

      return newMobs;
    } catch (err) {
      console.error('[mobStore] spawnFromBestiary failed:', err);
      return [];
    }
  },

  spawnAdHoc: async (params) => {
    console.warn('[mobStore] spawnAdHoc is deprecated — use spawnMob() instead');
    const { campaignId, ...mobParams } = params;
    try {
      const spawned = await trpc.bestiary.spawn.mutate({
        campaignId,
        count: 1,
        displayName: mobParams.name,
        ac: mobParams.ac,
        maxHp: mobParams.maxHp,
        abilities: {
          str: mobParams.strength ?? 10,
          dex: mobParams.dexterity ?? 10,
          con: mobParams.constitution ?? 10,
          int: mobParams.intelligence ?? 10,
          wis: mobParams.wisdom ?? 10,
          cha: mobParams.charisma ?? 10,
        },
        actions: mobParams.actions,
        speed: mobParams.speed,
        cr: mobParams.cr != null ? String(mobParams.cr) : undefined,
        size: mobParams.size,
        creatureType: mobParams.type,
        baseName: mobParams.name,
      });

      const em = spawned[0];
      const mob = dbToMob(em as unknown as DbEncounterMob);

      set((state) => {
        const newMobs = new Map(state.mobs);
        newMobs.set(mob.id, mob);
        const newCounters = new Map(state.instanceCounters);
        const currentCount = newCounters.get(mob.name) || 0;
        newCounters.set(mob.name, currentCount + 1);
        return { mobs: newMobs, instanceCounters: newCounters };
      });

      return mob;
    } catch (err) {
      console.error('[mobStore] spawnAdHoc failed, falling back to local:', err);
      // Fallback: create local-only mob (backward compat)
      return get().addMob(mobParams);
    }
  },

  clearEncounter: async (campaignId) => {
    // Clear initiative immediately (before async DB call) to prevent stale re-adds
    useInitiativeStore.getState().clearAllEntries();
    try {
      await trpc.bestiary.clearEncounter.mutate({ campaignId });
      // Clear all local mobs — realtime will re-sync named ones if any survive
      set({ mobs: new Map(), instanceCounters: new Map() });
    } catch (err) {
      console.error('[mobStore] clearEncounter failed:', err);
      // Fallback to local clear
      set({ mobs: new Map(), instanceCounters: new Map() });
    }
  },

  subscribeToRealtime: (campaignId) => {
    get().unsubscribeFromRealtime();
    set({ campaignId });

    const { cleanup } = createSubscription({
      channelName: `encounter-mobs:${campaignId}`,
      table: 'machina_encounter_mobs',
      filter: `campaign_id=eq.${campaignId}`,
      event: '*',
      onPayload: (payload) => {
        if (payload.eventType === 'INSERT') {
          const db = payload.new as unknown as DbEncounterMob;
          const mob = dbToMob(db);
          set((state) => {
            if (state.mobs.has(mob.id)) return state; // Already added optimistically
            const newMobs = new Map(state.mobs);
            newMobs.set(mob.id, mob);
            return { mobs: newMobs };
          });
          // Pre-populate iso sprite cache if this mob has saved sprites.
          seedIsoSpritesFromMob(mob);
        } else if (payload.eventType === 'UPDATE') {
          const db = payload.new as unknown as DbEncounterMob;
          const mob = dbToMob(db);
          // If iso_sprites were just saved on this mob, seed immediately so
          // players see the sprite without reload.
          seedIsoSpritesFromMob(mob);
          // Guard: skip overwriting the mob Map for mobs with pending local
          // mutations (our own optimistic state is newer than the DB echo).
          // We still push token visuals below so other clients always re-render.
          const hasPendingLocal = pendingSyncs.has(mob.id) || dirtyFields.has(mob.id);
          if (!hasPendingLocal) {
            set((state) => {
              const newMobs = new Map(state.mobs);
              newMobs.set(mob.id, mob);
              return { mobs: newMobs };
            });
          }
          // Sync token visuals — HP/death AND conditions. Conditions were
          // previously omitted here, so status icons set by the GM never
          // reached other clients' tokens (the local mob Map updated but the
          // token's `conditions` field — what TokenRenderer reads — did not).
          // Skip the token push entirely when we have newer local state, so we
          // don't clobber an optimistic local condition/HP edit with a stale echo.
          if (!hasPendingLocal) {
            const hpTier: HpTier = mob.dead ? 'dead' : getHpTier(mob.currentHp, mob.maxHp);
            useTokenStore.getState().syncFromCombatant(mob.id, {
              currentHp: mob.currentHp,
              maxHp: mob.maxHp,
              hpTier,
              conditions: mob.conditions.map((c) => ({
                type: c.type,
                source: c.sourceId,
                duration: c.duration ?? null,
                saveEndOfTurn: false,
              })),
            });
          }
        } else if (payload.eventType === 'DELETE') {
          const old = payload.old as { id?: string };
          if (old.id) {
            const deletedId = old.id;
            // Remove the mob's token(s) locally too. The matching
            // machina_tokens DELETE event normally handles this, but if that
            // row was never persisted (or its event is dropped) the corpse
            // would linger on other clients — the exact "delete persists"
            // bug. Belt-and-suspenders: nuke any token bound to this mob.
            const tokenStore = useTokenStore.getState();
            for (const [tokenId, token] of tokenStore.tokens) {
              if (token.combatantId === deletedId) {
                tokenStore.removeToken(tokenId);
              }
            }
            useInitiativeStore.getState().removeEntry(deletedId);
            set((state) => {
              if (!state.mobs.has(deletedId)) return state;
              const newMobs = new Map(state.mobs);
              newMobs.delete(deletedId);
              return { mobs: newMobs };
            });
          }
        }
      },
    });

    set({ realtimeCleanup: cleanup });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },

  // ===========================================================================
  // UNIFIED MOB PIPELINE
  // ===========================================================================

  spawnMob: async (params, options = {}) => {
    console.log('[mobStore] spawnMob called:', params.name, 'campaign:', params.campaignId);
    const { count = 1, position, rollInitiative = true, imageUrl } = options;
    const tokenStore = useTokenStore.getState();
    const gridSize = tokenStore.gridSize;

    // Step 1: DB insert via tRPC
    const abilities = params.abilities ?? { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 };
    let spawnedMobs: Mob[];

    try {
      console.log('[mobStore] calling bestiary.spawn.mutate...');
      const spawned = await trpc.bestiary.spawn.mutate({
        campaignId: params.campaignId,
        sceneId: params.sceneId ?? useCombatStore.getState().currentSceneId ?? undefined,
        bestiaryId: params.bestiaryId,
        count,
        displayName: params.displayName ?? params.name,
        ac: params.ac,
        maxHp: params.maxHp,
        abilities,
        actions: params.actions,
        resistances: params.resistances,
        immunities: params.immunities,
        conditionImmunities: params.conditionImmunities,
        speed: params.speed,
        cr: params.cr != null ? String(params.cr) : undefined,
        size: params.size,
        creatureType: params.type,
        baseName: params.name,
        open5eSlug: params.open5eSlug,
        imgUrl: params.imgUrl ?? imageUrl,
      });

      spawnedMobs = [];
      set((state) => {
        const newMobMap = new Map(state.mobs);
        const newCounters = new Map(state.instanceCounters);

        for (const em of spawned) {
          const mob = dbToMob(em as unknown as DbEncounterMob);
          newMobMap.set(mob.id, mob);
          spawnedMobs.push(mob);
          const bn = mob.name;
          const cc = newCounters.get(bn) || 0;
          newCounters.set(bn, cc + 1);
        }

        return { mobs: newMobMap, instanceCounters: newCounters };
      });
    } catch (err) {
      const msg = (err as Error).message || 'Unknown error';
      console.error('[mobStore] spawnMob DB insert failed — refusing to create local-only ghost mobs:', msg);

      // LOUD surfacing — the silent fallback was the root cause of today's
      // ghost-mob + HP-not-syncing bugs. Now we refuse to create mobs that
      // can't persist. Better to fail visibly than drift silently.
      try {
        const { useChatStore } = await import('./chatStore');
        useChatStore.getState().addSystemMessage?.(
          `❌ Spawn failed: ${params.name} × ${count}. The mobs were NOT created. Reason: ${msg}. Check auth / connection and try again.`
        );
      } catch { /* non-fatal */ }

      // Throw so the caller knows the spawn didn't happen. Previously we'd
      // return fake local mobs that looked successful but weren't in DB.
      throw new Error(`Spawn failed to persist: ${msg}`);
    }

    // Step 2: Create tokens
    console.log('[mobStore] spawnMob step 2: creating tokens for', spawnedMobs.length, 'mobs');

    const tokens: Token[] = [];
    // Use monotonic counter for grid offset — safe across concurrent spawnMob calls
    const startOffset = spawnPositionCounter;
    spawnPositionCounter += spawnedMobs.length;

    for (let i = 0; i < spawnedMobs.length; i++) {
      const mob = spawnedMobs[i];
      const col = (startOffset + i) % 3;
      const row = Math.floor((startOffset + i) / 3);
      const pos = position
        ? { x: position.x + col * gridSize, y: position.y + row * gridSize }
        : { x: gridSize * (12 + col), y: gridSize * (2 + row) };

      const resolvedImage = imageUrl ?? resolveMobImage(mob.name, params.imgUrl);

      const token = tokenStore.addToken({
        name: mob.displayName || mob.name,
        position: pos,
        combatantId: mob.id,
        currentHp: mob.currentHp,
        maxHp: mob.maxHp,
        ac: mob.ac,
        visuals: {
          label: (mob.displayName || mob.name).substring(0, 2).toUpperCase(),
          borderColor: '#d9534f',
          color: '#d9534f',
          shape: 'circle',
          size: (mob.size || 'medium') as 'tiny' | 'small' | 'medium' | 'large' | 'huge' | 'gargantuan',
          imageUrl: resolvedImage,
          showHpBar: true,
          showConditionIcons: true,
        },
      });
      tokens.push(token);
    }

    // Step 3: Add to encounter / initiative if combat is active
    // Use combatStore.setInitiative as single entry — it delegates to encounterStore
    // when an encounter is active, and handles legacy path otherwise.
    if (rollInitiative) {
      const combatStore = useCombatStore.getState();

      for (const mob of spawnedMobs) {
        const dexMod = getModifier(mob.dexterity);
        const initiative = Math.floor(Math.random() * 20) + 1 + dexMod;
        combatStore.setInitiative(mob.id, initiative, dexMod);
      }
    }

    // Step 4: Generate missing token images async
    const namesNeedingImages = spawnedMobs
      .filter(m => !resolveMobImage(m.name, params.imgUrl) && !imageUrl)
      .map(m => m.name);

    if (namesNeedingImages.length > 0) {
      generateMissingTokens(namesNeedingImages, (name, url) => {
        // Update all tokens with matching base name
        const ts = useTokenStore.getState();
        for (const t of ts.tokens.values()) {
          if (t.name.toLowerCase().startsWith(name.toLowerCase())) {
            ts.updateToken({ id: t.id, visuals: { ...t.visuals, imageUrl: url } });
          }
        }
      });
    }

    // Step 5: Seed iso sprite cache from bestiary template, if saved.
    // Each spawned instance gets the same starting sprites — GM can still regen
    // per-instance for variety from Menagerie.
    if (params.bestiaryId) {
      try {
        const { useBestiaryStore } = await import('./bestiaryStore');
        const { useIsoSpriteStore } = await import('./isoSpriteStore');
        const entry = useBestiaryStore.getState().entries.find((e) => e.id === params.bestiaryId);
        const templateSprites = entry?.isoSprites;
        if (templateSprites) {
          const isoStore = useIsoSpriteStore.getState();
          for (const mob of spawnedMobs) {
            for (const f of ['south', 'north', 'east', 'west'] as const) {
              const url = templateSprites[f];
              if (url) isoStore.setCustomSprite(mob.id, url, f, mob.displayName || mob.name);
            }
          }
        }
      } catch (err) {
        console.warn('[mobStore] iso sprite seed from bestiary failed:', err);
      }
    }

    return { mobs: spawnedMobs, tokens };
  },

  clearLocalState: () => {
    set({
      mobs: new Map(),
      instanceCounters: new Map(),
      campaignId: null,
    });
  },
}));
